import type { Infer } from 'superstruct';
import { array, assign, nullable, object, optional, string } from 'superstruct';

import {
	EditFacebookTemplateDto,
	ListFacebookTemplateSort,
	ViewFacebookTemplateDto,
} from '@adflow/shared/data-facebook-ads';
import {
	EditGoogleAdsTemplateDto,
	ListGoogleAdsTemplateSort,
	ViewGoogleAdsTemplateDto,
} from '@adflow/shared/data-google-ads';
import {
	EditGMBTemplateDto,
	ListGMBTemplateSort,
	ViewGMBTemplateDto,
} from '@adflow/shared/data-google-my-business';
import { ListResourceDto, PaginationQuery } from '@adflow/shared/util-data';
import { adflowApiSchema } from '@adflow/shared/util-route-schema';
import { parsableInteger } from '@adflow/shared/util-superstruct';

import {
	ViewHistoryFacebookTemplateDto,
	ViewHistoryFacebookTemplateUploadDto,
} from './history-facebook/history-facebook-template.dto';
import { ListHistoryFacebookTemplateSort } from './history-facebook/list-history-facebook-template-query.dto';
import {
	ViewHistoryGMBTemplateDto,
	ViewHistoryGMBTemplateUploadDto,
} from './history-gmb/history-gmb-template.dto';
import { ListHistoryGMBTemplateSort } from './history-gmb/list-history-gmb-template-query.dto';
import {
	ViewHistoryGoogleAdsTemplateDto,
	ViewHistoryGoogleAdsTemplateUploadDto,
} from './history-google-ads/history-google-ads-template.dto';
import { ListHistoryGoogleAdsTemplateSort } from './history-google-ads/list-history-google-ads-template-query.dto';

const p = adflowApiSchema;

export const ListGoogleAdsTemplatesHistoryQuery =
	ListHistoryGoogleAdsTemplateSort;

export type ListGoogleAdsTemplatesHistoryQuery = Infer<
	typeof ListGoogleAdsTemplatesHistoryQuery
>;

export const GetGoogleAdsTemplateHistory = p.get(
	'google-ads/templates/:templateId/history',
	{
		req: object({
			params: object({ templateId: parsableInteger() }),
			query: ListGoogleAdsTemplatesHistoryQuery,
		}),
		res: array(ViewHistoryGoogleAdsTemplateDto),
	}
);

export const ListGoogleAdsTemplatesQuery = assign(
	PaginationQuery,
	ListGoogleAdsTemplateSort,
	object({ search: optional(string()) })
);

export type ListGoogleAdsTemplatesQuery = Infer<
	typeof ListGoogleAdsTemplatesQuery
>;

export const ViewGoogleAdsTemplateDtoWithUploadResult = assign(
	ViewGoogleAdsTemplateDto,
	object({
		uploadResult: optional(nullable(ViewHistoryGoogleAdsTemplateUploadDto)),
	})
);

export type ViewGoogleAdsTemplateDtoWithUploadResult = Infer<
	typeof ViewGoogleAdsTemplateDtoWithUploadResult
>;

export const ListGoogleAdsTemplatesWithUploadResult = p.get(
	'google-ads/templates',
	{
		req: object({
			query: ListGoogleAdsTemplatesQuery,
		}),
		res: ListResourceDto(ViewGoogleAdsTemplateDtoWithUploadResult),
	}
);

export const GetGoogleAdsTemplate = p.get('google-ads/templates/:templateId', {
	req: object({
		params: object({ templateId: parsableInteger() }),
	}),
	res: ViewGoogleAdsTemplateDtoWithUploadResult,
});

export const EditGoogleAdsTemplate = p.post(
	'google-ads/templates/:templateId',
	{
		req: object({
			params: object({ templateId: parsableInteger() }),
			body: EditGoogleAdsTemplateDto,
		}),
		res: ViewGoogleAdsTemplateDtoWithUploadResult,
	}
);

// Facebook Template History Routes
export const ListFacebookTemplatesHistoryQuery =
	ListHistoryFacebookTemplateSort;

export type ListFacebookTemplatesHistoryQuery = Infer<
	typeof ListFacebookTemplatesHistoryQuery
>;

export const GetFacebookTemplateHistory = p.get(
	'facebook/templates/:templateId/history',
	{
		req: object({
			params: object({ templateId: parsableInteger() }),
			query: ListFacebookTemplatesHistoryQuery,
		}),
		res: array(ViewHistoryFacebookTemplateDto),
	}
);

export const ListFacebookTemplatesQuery = assign(
	PaginationQuery,
	ListFacebookTemplateSort,
	object({ search: optional(string()) })
);

export type ListFacebookTemplatesQuery = Infer<
	typeof ListFacebookTemplatesQuery
>;

export const ViewFacebookTemplateDtoWithUploadResult = assign(
	ViewFacebookTemplateDto,
	object({
		uploadResult: optional(nullable(ViewHistoryFacebookTemplateUploadDto)),
	})
);

export type ViewFacebookTemplateDtoWithUploadResult = Infer<
	typeof ViewFacebookTemplateDtoWithUploadResult
>;

export const ListFacebookTemplatesWithUploadResult = p.get(
	'facebook/templates',
	{
		req: object({
			query: ListFacebookTemplatesQuery,
		}),
		res: ListResourceDto(ViewFacebookTemplateDtoWithUploadResult),
	}
);

export const GetFacebookTemplate = p.get('facebook/templates/:templateId', {
	req: object({
		params: object({ templateId: parsableInteger() }),
	}),
	res: ViewFacebookTemplateDtoWithUploadResult,
});

export const EditFacebookTemplate = p.post('facebook/templates/:templateId', {
	req: object({
		params: object({ templateId: parsableInteger() }),
		body: EditFacebookTemplateDto,
	}),
	res: ViewFacebookTemplateDtoWithUploadResult,
});

// GMB Template History Routes
export const ListGMBTemplatesHistoryQuery = ListHistoryGMBTemplateSort;

export type ListGMBTemplatesHistoryQuery = Infer<
	typeof ListGMBTemplatesHistoryQuery
>;

export const GetGMBTemplateHistory = p.get(
	'google-my-business/templates/:templateId/history',
	{
		req: object({
			params: object({ templateId: parsableInteger() }),
			query: ListGMBTemplatesHistoryQuery,
		}),
		res: array(ViewHistoryGMBTemplateDto),
	}
);

export const ListGMBTemplatesQuery = assign(
	PaginationQuery,
	ListGMBTemplateSort,
	object({ search: optional(string()) })
);

export type ListGMBTemplatesQuery = Infer<typeof ListGMBTemplatesQuery>;

export const ViewGMBTemplateDtoWithUploadResult = assign(
	ViewGMBTemplateDto,
	object({
		uploadResult: optional(nullable(ViewHistoryGMBTemplateUploadDto)),
	})
);

export type ViewGMBTemplateDtoWithUploadResult = Infer<
	typeof ViewGMBTemplateDtoWithUploadResult
>;

export const ListGMBTemplatesWithUploadResult = p.get(
	'google-my-business/templates',
	{
		req: object({
			query: ListGMBTemplatesQuery,
		}),
		res: ListResourceDto(ViewGMBTemplateDtoWithUploadResult),
	}
);

export const GetGMBTemplate = p.get(
	'google-my-business/templates/:templateId',
	{
		req: object({
			params: object({ templateId: parsableInteger() }),
		}),
		res: ViewGMBTemplateDtoWithUploadResult,
	}
);

export const EditGMBTemplate = p.post(
	'google-my-business/templates/:templateId',
	{
		req: object({
			params: object({ templateId: parsableInteger() }),
			body: EditGMBTemplateDto,
		}),
		res: ViewGMBTemplateDtoWithUploadResult,
	}
);
